import { motion } from "framer-motion";
import { get } from "lodash-es";
import { AlertCircle } from "lucide-react";
import { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { matchPath, useNavigate } from "react-router-dom";

import { Card } from "@/components/card.tsx";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Creatable } from "@/components/ui/creatable.tsx";
import { DialogBody, DialogClose, DialogFooter } from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { labelVariants } from "@/components/ui/label.tsx";
import { MultiValueInput } from "@/components/ui/react-select.tsx";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select.tsx";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch.tsx";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useGetOrganisations } from "@/features/organisations/api/get-organisations.tsx";
import { useOrganisation } from "@/features/select-organisation/api/get-my-organisation.tsx";
import { queryClient } from "@/lib/react-query";
import { cn } from "@/lib/utils.ts";
import { HttpError, isBadRequest } from "@/types/error";
import { RoleEnum, RoleGroupEnum } from "@classview/shared-types";
import { zodResolver } from "@hookform/resolvers/zod";

import { useCreateInvitation } from "../api/add-invitation.tsx";
import {
  AddInvitationFormData,
  addInvitationSchema,
} from "../types/add-invitation-schema";

type AddInvitationFormProps = {
  onClose: () => void;
  /**
   * If provided, cannot select an organisation
   */
  organisationId?: string;
};

export function AddInvitationForm(props: AddInvitationFormProps) {
  const { onClose, organisationId } = props;
  const errorRef = useRef<HTMLDivElement | null>(null);
  const { organisation } = useOrganisation();
  const { data: organisations, isPending: isLoadingOrganisations } =
    useGetOrganisations();

  const form = useForm<AddInvitationFormData>({
    resolver: zodResolver(addInvitationSchema),
    shouldFocusError: true,
    defaultValues: {
      organisationId: organisationId || organisation.id,
      emails: [],
      roleGroup: RoleGroupEnum.USER,
      organisationManagement: false,
      userManagement: false,
      jobPosting: false,
      eventManagement: false,
    },
  });

  const navigate = useNavigate();

  const {
    mutateAsync: createInvitation,
    isSuccess,
    isError,
  } = useCreateInvitation({
    onSuccess: async () => {
      if (matchPath("/settings/invitations", window.location.pathname)) {
        //non-admin view
        await queryClient.invalidateQueries({
          queryKey: ["invitations", organisationId],
          exact: false,
        });

        return;
      }

      if (matchPath("/settings/users", window.location.pathname)) {
        //non-admin view
        await queryClient.invalidateQueries({
          queryKey: ["invitations", organisationId],
          exact: false,
        });
        navigate("/settings/invitations");

        return;
      }

      //super-admin view
      await queryClient.invalidateQueries({
        queryKey: ["invitations", organisationId ?? "sa-view"],
        exact: false,
      });

      if (
        matchPath("/admin/organisations/:id/users", window.location.pathname)
      ) {
        navigate(`/admin/organisations/${organisationId}/invitations`);
      }
    },
  });

  useEffect(() => {
    if (isError) {
      if (errorRef.current) {
        errorRef.current.scrollIntoView({
          behavior: "smooth",
          block: "end",
          inline: "nearest",
        });
      }
    }
  }, [isError]);

  const roleGroup = form.watch("roleGroup");

  async function handleCreateInvitation(data: AddInvitationFormData) {
    try {
      const additionalRoles: RoleEnum[] = [];
      if (data.organisationManagement) {
        additionalRoles.push(RoleEnum.ORGANISATION_MANAGEMENT);
      }

      if (data.userManagement) {
        additionalRoles.push(RoleEnum.USER_MANAGEMENT);
      }

      if (data.jobPosting) {
        additionalRoles.push(RoleEnum.JOB_CREATE);
      }

      if (data.eventManagement) {
        additionalRoles.push(RoleEnum.EVENTS_CREATE);
      }

      await createInvitation({
        data: {
          emails: data.emails.map((email) => email.value.toLowerCase().trim()),
          roleGroup: data.roleGroup,
          additionalRoles:
            additionalRoles.length > 0 ? additionalRoles : undefined,
        },
        organisationId: data.organisationId,
      });

      onClose();
    } catch (error) {
      const httpError = error as HttpError;
      if (isBadRequest(httpError)) {
        const errorMessage = httpError.message.map((error) => {
          if (error.includes("emails.")) {
            const match = error.match(/emails\.(\d+)/);
            const number = match ? Number(match[1]) : null;

            const invalidEmail: { label: string; value: string } | undefined =
              get(data, `emails.${number}`);

            if (invalidEmail) {
              return error.replace(`emails.${number}`, invalidEmail.value);
            }
          }
          return error;
        });
        form.setError("root", { message: errorMessage.join(", ") });
      } else {
        console.error("Unexpected error:", error);
        form.setError("root", {
          message: "An unexpected error occurred. Please try again.",
        });
      }
    }
  }

  const isInitialRender = useRef(true);

  // Handles updating the form when the role group is changed
  useEffect(() => {
    // If it's the initial render, we don't need to update the form
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }

    // If the role group has changed, we need to update the form and disable all the permissions
    form.setValue("organisationManagement", false);
    form.setValue("userManagement", false);
    form.setValue("jobPosting", false);
    form.setValue("eventManagement", false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleGroup]);

  const isPermissionEditorOpen = [
    RoleGroupEnum.ADMIN,
    RoleGroupEnum.USER,
  ].includes(roleGroup);

  const selectedOrganisationId = form.watch("organisationId");

  const selectedOrganisation = organisations?.find(
    (organisation) => organisation.id === selectedOrganisationId,
  );

  const isJobPostingToggleVisible =
    selectedOrganisation?.license?.isGivenMarketplaceAccess;
  const isEventManagementToggleVisible =
    selectedOrganisation?.license?.isGivenEventAccess;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleCreateInvitation)}>
        <DialogBody>
          {!organisationId && (
            <FormField
              control={form.control}
              name="organisationId"
              render={({ field }) => (
                <FormItem className="mb-4">
                  <FormLabel isRequired>Organisation</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                    disabled={isLoadingOrganisations}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select an organisation..." />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {organisations?.map((organisation) => (
                        <SelectItem
                          key={organisation.id}
                          value={organisation.id}
                        >
                          {organisation.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          <p className="text-md font-semibold text-foreground">Users</p>
          <p className="mb-6 text-xs text-muted">
            You can invite multiple users using a comma.
          </p>

          <FormField
            control={form.control}
            name="emails"
            render={({ field }) => (
              <FormItem>
                <FormLabel isRequired>Emails</FormLabel>
                <FormControl>
                  <Creatable
                    placeholder="jim.doe@college.ac.uk"
                    isMulti
                    components={{
                      Input: MultiValueInput,
                      DropdownIndicator: () => null,
                      ClearIndicator: () => null,
                      IndicatorSeparator: () => null,
                      Menu: () => null,
                    }}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Separator className="my-6" />
          <p className="text-md font-semibold text-foreground">Permissions</p>
          <p className="mb-6 text-xs text-muted">
            What permission level should self service users initially join as?
            Permissions can be manually updated on a per-user basis at any time.
          </p>
          <FormField
            control={form.control}
            name="roleGroup"
            render={({ field }) => (
              <FormItem>
                <FormLabel>User Role</FormLabel>
                <FormDescription>
                  Give users a broad allocation of permissions
                </FormDescription>
                <FormControl>
                  <ToggleGroup
                    type="single"
                    onValueChange={field.onChange}
                    value={field.value}
                    className="relative flex gap-x-0 divide-x divide-border rounded-md border border-border shadow-sm"
                  >
                    <ToggleGroupItem
                      value={RoleGroupEnum.VIEWER}
                      variant="ghost"
                    >
                      Viewer
                    </ToggleGroupItem>
                    <ToggleGroupItem value={RoleGroupEnum.USER} variant="ghost">
                      User
                    </ToggleGroupItem>
                    {organisation.roleGroup === RoleGroupEnum.ADMIN && (
                      <ToggleGroupItem
                        value={RoleGroupEnum.ADMIN}
                        variant="ghost"
                      >
                        Admin
                      </ToggleGroupItem>
                    )}
                  </ToggleGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {roleGroup === RoleGroupEnum.VIEWER && (
            <Card className="mt-4 rounded-md px-3 py-2">
              <p className="mb-0 text-xs text-muted">
                This role is ideal for users who only need to view content
                without making any changes.
              </p>
            </Card>
          )}
          {roleGroup === RoleGroupEnum.USER && (
            <Card className="mt-4 rounded-md px-3 py-2">
              <p className="mb-0 text-xs text-muted">
                This role is designed for users who require standard access to
                features, allowing interaction with the application but without
                administrative privileges.
              </p>
            </Card>
          )}
          {roleGroup === RoleGroupEnum.ADMIN && (
            <Card className="mt-4 rounded-md px-3 py-2">
              <p className="mb-0 text-xs text-muted">
                This role suits the majority of users, giving them access to
                most features, without edit permissions.
              </p>
            </Card>
          )}
          <motion.div
            className="overflow-hidden"
            initial={{ opacity: 0, height: 0 }}
            animate={
              isPermissionEditorOpen
                ? { opacity: 1, height: "auto" }
                : { opacity: 0, height: 0 }
            }
            transition={{ duration: 0.3 }}
          >
            {
              <div>
                <Separator className="my-6" />
                <p className={cn(labelVariants(), "mb-2")}>
                  Organisation Management
                </p>
                <p className="mb-4 text-sm text-muted-foreground">
                  Users are able to access organisational settings and
                  management.
                </p>
                {roleGroup === RoleGroupEnum.ADMIN && (
                  <FormField
                    control={form.control}
                    name="organisationManagement"
                    render={({ field }) => (
                      <FormItem className="mb-2 flex flex-row items-center">
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <div className="ml-2">
                          <FormLabel className="text-base">
                            Organisations Management
                          </FormLabel>
                          <FormDescription>
                            User can manage organisation details
                          </FormDescription>
                        </div>
                      </FormItem>
                    )}
                  />
                )}
                {roleGroup === RoleGroupEnum.USER && (
                  <FormField
                    control={form.control}
                    name="userManagement"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center">
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <div className="ml-2">
                          <FormLabel className="text-base">
                            User Management
                          </FormLabel>
                          <FormDescription>
                            The user is allowed to manage existing users or
                            invite new users to the organisation.
                          </FormDescription>
                        </div>
                      </FormItem>
                    )}
                  />
                )}
                {roleGroup !== RoleGroupEnum.VIEWER && (
                  <>
                    {isJobPostingToggleVisible && (
                      <>
                        <Separator className="my-6" />
                        <p className={cn(labelVariants(), "mb-2")}>
                          Job Posting
                        </p>
                        <p className="mb-4 text-sm text-muted-foreground">
                          Users are permitted to advertise positions that they
                          are either looking to fill (hire for), or additional
                          resource they are looking to offload.
                        </p>
                        <FormField
                          control={form.control}
                          name="jobPosting"
                          render={({ field }) => (
                            <FormItem className="flex flex-row items-center">
                              <FormControl>
                                <Switch
                                  checked={field.value}
                                  onCheckedChange={field.onChange}
                                />
                              </FormControl>
                              <div className="ml-2">
                                <FormLabel className="text-base">
                                  Access Enabled
                                </FormLabel>
                                <FormDescription>
                                  Users can access this feature
                                </FormDescription>
                              </div>
                            </FormItem>
                          )}
                        />
                      </>
                    )}
                    {isEventManagementToggleVisible && (
                      <>
                        <Separator className="my-6" />
                        <p className={cn(labelVariants(), "mb-2")}>
                          Event Management
                        </p>
                        <p className="mb-4 text-sm text-muted-foreground">
                          Users are permitted to create and manage events.
                        </p>
                        <FormField
                          control={form.control}
                          name="eventManagement"
                          render={({ field }) => (
                            <FormItem className="flex flex-row items-center">
                              <FormControl>
                                <Switch
                                  checked={field.value}
                                  onCheckedChange={field.onChange}
                                />
                              </FormControl>
                              <div className="ml-2">
                                <FormLabel className="text-base">
                                  Access Enabled
                                </FormLabel>
                                <FormDescription>
                                  Users can access this feature
                                </FormDescription>
                              </div>
                            </FormItem>
                          )}
                        />
                      </>
                    )}
                  </>
                )}
              </div>
            }
          </motion.div>
          {form.formState.errors.root && (
            <>
              <Separator className="my-6" />
              <Alert variant="destructive" ref={errorRef}>
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Validation Error</AlertTitle>
                <AlertDescription>
                  {form.formState.errors.root.message}
                </AlertDescription>
              </Alert>
            </>
          )}
        </DialogBody>

        <DialogFooter className="sm:justify-end">
          <DialogClose asChild>
            <Button type="button" variant="outline">
              Close
            </Button>
          </DialogClose>
          <Button
            type="submit"
            isLoading={form.formState.isSubmitting || isSuccess}
            isAnimated
          >
            Invite User
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}
